import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Brain, Target } from 'lucide-react';

// Tailwind classes per confidence level. Centralised so the card stays
// visually consistent with the rest of the QuoteSnapp dashboard.
const STYLES = {
  'sweet-spot':   { bar: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700',
                    bg: 'bg-emerald-50/70', icon: Target,        iconColor: 'text-emerald-600',
                    label: 'Sweet spot' },
  'underpriced':  { bar: 'bg-blue-500',    ring: 'ring-blue-200',    text: 'text-blue-700',
                    bg: 'bg-blue-50/70',    icon: TrendingDown,  iconColor: 'text-blue-600',
                    label: 'Likely win' },
  'caution':      { bar: 'bg-amber-500',   ring: 'ring-amber-200',   text: 'text-amber-700',
                    bg: 'bg-amber-50/70',   icon: TrendingUp,    iconColor: 'text-amber-600',
                    label: 'High markup' },
  'high-risk':    { bar: 'bg-rose-500',    ring: 'ring-rose-200',    text: 'text-rose-700',
                    bg: 'bg-rose-50/70',    icon: TrendingUp,    iconColor: 'text-rose-600',
                    label: 'High risk' },
};

export const QuoteConfidenceCard = ({ confidence }) => {
  if (!confidence || confidence.state !== 'scored') {
    // Hide silently when there's not enough training data — no point in adding
    // visual noise to the quote builder for first-time users.
    return null;
  }

  const s = STYLES[confidence.level] || STYLES['sweet-spot'];
  const Icon = s.icon;
  const win = Math.max(0, Math.min(100, confidence.win_probability));

  return (
    <Card className={`border ring-2 ${s.ring} ${s.bg}`} data-testid="quote-confidence-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-9 h-9 rounded-lg bg-white flex items-center justify-center ${s.iconColor}`}>
            <Brain className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm">Quote Confidence</span>
              <Badge className={`${s.bar} hover:${s.bar} text-white text-xs flex items-center gap-1`}>
                <Icon className="w-3 h-3" /> {s.label}
              </Badge>
            </div>
            <p className={`text-xs ${s.text} mt-0.5`} data-testid="quote-confidence-message">
              {confidence.message}
            </p>
          </div>
        </div>

        {/* Win probability bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-600">Estimated win probability</span>
            <span className="font-semibold text-slate-900" data-testid="quote-confidence-win-prob">{win}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${s.bar} transition-all`}
              style={{ width: `${win}%` }}
              data-testid="quote-confidence-bar"
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
            <span>Based on {confidence.sample_size} similar closed deal{confidence.sample_size !== 1 ? 's' : ''}</span>
            <span>
              {confidence.delta_percent > 0 ? '+' : ''}{confidence.delta_percent}% vs. avg
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuoteConfidenceCard;
